import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import TransactionFlowDefaultTemplate from "../../others/transactionFlowDefaultTemplate";
import { useDispatch, useSelector } from "react-redux";
import { setHighlight } from "../../../store/slices/misc/highlightSlice";
import { Button, Tag } from "antd";
import {
  ReloadOutlined,
  SearchOutlined,
  DownloadOutlined,
  EyeOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { Tooltip } from "antd";
const ExcelExportRequestedList = () => {
  const [refreshTrigger, setRefreshTrigger] = useState(false);
  const dispatch = useDispatch();
  const downloadHighlighted = useSelector(
    (state) => state.highlight.downloadHighlighted
  );
  useEffect(() => {
    if (downloadHighlighted) {
      // Reset the highlight state to false
      dispatch(setHighlight(false));
    }
  }, [downloadHighlighted, dispatch]);

  const getStatusTag = (status) => {
    const statusConfig = {
      Requested: {
        color: "warning",
        icon: <ClockCircleOutlined />,
        text: "Requested",
      },
      Processed: {
        color: "processing",
        icon: <SyncOutlined />,
        text: "Processed",
      },
      Processing: {
        color: "processing",
        icon: <SyncOutlined spin />,
        text: "Processing",
      },
      "Error In File": {
        color: "error",
        icon: <CloseCircleOutlined />,
        text: "Error In File",
      },
      "Created Successfully": {
        color: "success",
        icon: <CheckCircleOutlined />,
        text: "Created Successfully",
      },
      Validated: {
        color: "success",
        icon: <CheckCircleOutlined />,
        text: "Validated",
      },
      Error: {
        color: "error",
        icon: <CloseCircleOutlined />,
        text: "Error",
      },
      Failed: {
        color: "error",
        icon: <CloseCircleOutlined />,
        text: "Failed",
      },
    };
    const config = statusConfig[status];
    return (
      <Tag icon={config?.icon} color={config?.color}>
        {config?.text}
      </Tag>
    );
  };

  const getTypeTag = (type) => {
    const typeMap = {
      EXCEL: { text: "Excel", color: "orange" },

      // Masters & Lists (from reportConfiguration)
      Orders: { text: "Orders", color: "blue" },
      Invoices: { text: "Invoices", color: "purple" },
      "Contractor Payments": { text: "Contractor Payments", color: "volcano" },
      Materials: { text: "Materials", color: "gold" },
      Suppliers: { text: "Suppliers", color: "magenta" },
      "Purchase Requests": { text: "Purchase Requests", color: "cyan" },
      Budgets: { text: "Budgets", color: "lime" },
      Quotations: { text: "Quotations", color: "geekblue" },
      "Work Order List": { text: "Work Order List", color: "purple" },
      "Work Execution": { text: "Work Execution", color: "blue" },
      "RaBills List": { text: "RaBills List", color: "magenta" },
      "Work Boq list": { text: "Work Boq list", color: "cyan" },

      // Purchase Reports (from reportConfiguration)
      "Purchase by Site": { text: "Purchase by Site", color: "green" },
      "Purchase by Site Details": { text: "Purchase by Site Details", color: "green" },
      "Awarded To Supplier": { text: "Awarded To Supplier", color: "orange" },
      "Awarded To Supplier Details": { text: "Awarded To Supplier Details", color: "orange" },
      "Purchase Order": { text: "Purchase Order", color: "blue" },
      "Invoice by Supplier": { text: "Invoice by Supplier", color: "purple" },
      "Purchase by Material": { text: "Purchase by Material", color: "gold" },
      "Purchase by Material Details": { text: "Purchase by Material Details", color: "gold" },
      "Purchase by Category": { text: "Purchase by Category", color: "geekblue" },
      "Purchase by Category Details": { text: "Purchase by Category Details", color: "geekblue" },

      // Inventory & Stock (from reportConfiguration)
      "GRN Details List": { text: "GRN Details List", color: "cyan" },
      "Inventory Summary": { text: "Inventory Summary", color: "lime" },
      "Inventory Value": { text: "Inventory Value", color: "green" },
      "Material Moment": { text: "Material Moment", color: "gold" },

      // Supplier & Payment Aging (from reportConfiguration)
      "Supplier Balances": { text: "Supplier Balances", color: "red" },
      "Supplier Balances Details": { text: "Supplier Balances Details", color: "red" },
      "Payments Aging Summary": { text: "Payments Aging Summary", color: "volcano" },
      "Payments Aging Summary Details": { text: "Payments Aging Summary Details", color: "volcano" },
      "Payments Aging": { text: "Payments Aging", color: "orange" },
      "Payments Report": { text: "Payments Report", color: "volcano" },
      "Payments Report Details": { text: "Payments Report Details", color: "volcano" },

      // Material Movement & Utilization (from reportConfiguration)
      "Materials Issued To Site": { text: "Materials Issued To Site", color: "blue" },
      "Budget Tracking": { text: "Budget Tracking", color: "lime" },
      "Material Utilization Summary": { text: "Material Utilization Summary", color: "gold" },
      "Avg Material Utilized Breakup": { text: "Avg Material Utilized Breakup", color: "gold" },
      "Material Request Summary": { text: "Material Request Summary", color: "cyan" },
      "Material Issue Summary": { text: "Material Issue Summary", color: "cyan" },
      "Material Utilized Summary": { text: "Material Utilized Summary", color: "gold" },
      "Material Damage Summary": { text: "Material Damage Summary", color: "red" },
      "Material Movement Summary": { text: "Material Movement Summary", color: "geekblue" },
      "Avg Material Movement Breakup": { text: "Avg Material Movement Breakup", color: "geekblue" },

      // Work Order & WOM Reports (from reportConfiguration)
      "Work Order Portfolio Summary": { text: "Work Order Portfolio Summary", color: "purple" },
      "Work Order Status Distribution": { text: "Work Order Status Distribution", color: "cyan" },
      "Executed WOs Pending": { text: "Executed WOs Pending", color: "orange" },
      "Executed WOs Approved": { text: "Executed WOs Approved", color: "green" },
      "Payments Released Summary": { text: "Payments Released Summary", color: "volcano" },
      "Payments Released Vouchers": { text: "Payments Released Vouchers", color: "volcano" },
      "Work Order Variance Summary": { text: "Work Order Variance Summary", color: "red" },
      "Contractor Acceptance Tracker": { text: "Contractor Acceptance Tracker", color: "geekblue" },
      "Work Execution Pending Updates": { text: "Work Execution Pending Updates", color: "blue" },
      "Work Execution Approved vs Remaining": { text: "Work Execution Approved vs Remaining", color: "blue" },
      "Version Control Amendments Register": { text: "Version Control Amendments Register", color: "purple" },
      "RA Bills Status Register": { text: "RA Bills Status Register", color: "magenta" },
      "RA Bill Aging": { text: "RA Bill Aging", color: "volcano" },
      "Deductions Ledger": { text: "Deductions Ledger", color: "red" },
      "Payments Register": { text: "Payments Register", color: "green" },
      "Outstanding Aging": { text: "Outstanding Aging", color: "red" },
      "Contractor Due Amount": { text: "Contractor Due Amount", color: "orange" },
      "Contractor Due Amount Drilldown": { text: "Contractor Due Amount Drilldown", color: "orange" },
      "WO Vs Budget": { text: "WO Vs Budget", color: "lime" },
      "Contractor Ledger": { text: "Contractor Ledger", color: "purple" },
    };

    const typeInfo = typeMap[type] || { text: type, color: "default" };
    return <Tag color={typeInfo.color}>{typeInfo.text}</Tag>;
  };

  const columns = [
    {
      title: "File ID",
      dataIndex: "reportId",
      key: "reportId",
      width: 200,
    },
    {
      title: "File Type",
      dataIndex: "fileType",
      key: "fileType",
      width: 200,
      render: () => getTypeTag("EXCEL"),
    },
    {
      title: "File Name",
      dataIndex: "reportType",
      key: "reportType",
      width: 250,
      render: (type) => getTypeTag(type),
    },
    {
      title: "Date & Time",
      dataIndex: "requestedDate",
      key: "requestedDate",
      width: 200,
      render: (text) => (
        <span>{dayjs(text).format("DD MM YYYY HH:mm")}</span>
      ),
    },
    {
      title: "Comments",
      dataIndex: "comments",
      key: "comments",
      width: 200,
      render: (text) => (
        <Tooltip title={text}>
          <span
            style={{
              maxWidth: 130,
              display: "inline-block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {text || "-"}
          </span>
        </Tooltip>
      ),
    },
    {
      title: "File Status",
      dataIndex: "status",
      key: "status",
      width: 200,
      render: (status, record) => {
        if (status === "Failed" && record.error) {
          return <Tooltip title={record.error}>{getStatusTag(status)}</Tooltip>;
        }
        return getStatusTag(status);
      },
    },
    {
      title: "File Size (KB)",
      dataIndex: "fileSize",
      key: "fileSize",
      width: 200,
    },
    {
      title: "Download Link",
      dataIndex: "filePath",
      key: "filePath",
      width: 200,
      render: (text, record) =>
        record?.status === "Processed" ? (
          <Tooltip title="Download File">
            <Button
              type="text"
              icon={<DownloadOutlined />}
              href={text}
              target="_blank"
              style={{ padding: 0 }}
            />
          </Tooltip>
        ) : null,
    },
  ];

  return (
    <TransactionFlowDefaultTemplate
      moduleKey="excelexport-requested-list"
      title="Excel Export Requested"
      columns={columns}
      refresh={refreshTrigger}
      topRightJSX={
        <Button
          icon={<ReloadOutlined />}
          onClick={() => setRefreshTrigger(!refreshTrigger)}
        >
          Refresh
        </Button>
      }
      extra={{
        tableScrollX: 1300,
        showSearch: true,
      }}
    />
  );
};

export default ExcelExportRequestedList;
